import React, { useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Mail, MapPin, Send, CheckCircle, Loader2 } from 'lucide-react';
import { companyInfo } from '../data/mock';
import { toast } from 'sonner';
import { useLanguage } from '../context/LanguageContext';
import { translations } from '../data/translations';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ContactSection = () => {
  const { language } = useLanguage();
  const t = translations[language];
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      await axios.post(`${API}/contact`, { ...formData, language });
      
      toast.success(t.contact.toast.success, {
        description: t.contact.toast.successDescription
      });
      
      setFormData({ name: '', email: '', subject: '', message: '' });
    } catch (error) {
      toast.error(language === 'es' ? 'Error al enviar el mensaje' : 'Failed to send message', {
        description: language === 'es' ? 'Por favor intente de nuevo' : 'Please try again'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const contactInfo = [
    { icon: Mail, label: t.contact.info.email, value: companyInfo.email },
    { icon: MapPin, label: t.contact.info.address, value: companyInfo.address },
  ];

  return (
    <section id="contact" className="bg-[#1a1c1b] py-24 md:py-32">
      <div className="max-w-[87.5rem] mx-auto px-10">
        {/* Section Header */}
        <div className="text-center mb-16">
          <span className="text-[#888680] text-sm font-semibold uppercase tracking-widest mb-4 block">
            {t.contact.label}
          </span>
          <h2 className="font-black text-[#7dd3fc] uppercase leading-[0.85] mb-6" style={{ fontSize: 'clamp(2.5rem, 4vw + 1rem, 5rem)' }}>
            {t.contact.headline}
          </h2>
          <p className="text-[#7dd3fc]/70 text-xl max-w-2xl mx-auto">
            {t.contact.description}
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Contact Form */}
          <div className="bg-[#302f2c] border border-[#1e3a5f]/50 p-8 md:p-10">
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="text-[#7dd3fc] text-sm font-semibold uppercase tracking-wider mb-2 block">
                    {t.contact.form.name}
                  </label>
                  <Input
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder={t.contact.form.namePlaceholder}
                    required
                    className="bg-[#1a1c1b] border-[#1e3a5f] text-[#7dd3fc] placeholder:text-[#888680]/50 focus:border-[#7dd3fc] focus:ring-[#7dd3fc]/20 h-12"
                  />
                </div>
                <div>
                  <label className="text-[#7dd3fc] text-sm font-semibold uppercase tracking-wider mb-2 block">
                    {t.contact.form.email}
                  </label>
                  <Input
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder={t.contact.form.emailPlaceholder}
                    required
                    className="bg-[#1a1c1b] border-[#1e3a5f] text-[#7dd3fc] placeholder:text-[#888680]/50 focus:border-[#7dd3fc] focus:ring-[#7dd3fc]/20 h-12"
                  />
                </div>
              </div>
              <div>
                <label className="text-[#7dd3fc] text-sm font-semibold uppercase tracking-wider mb-2 block">
                  {t.contact.form.subject}
                </label>
                <Input
                  name="subject"
                  value={formData.subject}
                  onChange={handleChange}
                  placeholder={t.contact.form.subjectPlaceholder}
                  required
                  className="bg-[#1a1c1b] border-[#1e3a5f] text-[#7dd3fc] placeholder:text-[#888680]/50 focus:border-[#7dd3fc] focus:ring-[#7dd3fc]/20 h-12"
                />
              </div>
              <div>
                <label className="text-[#7dd3fc] text-sm font-semibold uppercase tracking-wider mb-2 block">
                  {t.contact.form.message}
                </label>
                <Textarea
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  placeholder={t.contact.form.messagePlaceholder}
                  required
                  rows={5}
                  className="bg-[#1a1c1b] border-[#1e3a5f] text-[#7dd3fc] placeholder:text-[#888680]/50 focus:border-[#7dd3fc] focus:ring-[#7dd3fc]/20 resize-none"
                />
              </div>
              <Button
                type="submit"
                disabled={isSubmitting}
                data-testid="contact-submit-btn"
                className="w-full bg-[#7dd3fc] text-[#1a1c1b] hover:bg-[#7dd3fc]/90 rounded-full px-8 py-6 font-semibold uppercase tracking-wide text-base transition-transform hover:scale-[1.02] active:scale-[0.98] disabled:opacity-70 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    {t.contact.form.submitting}
                  </>
                ) : (
                  <>
                    <Send size={18} />
                    {t.contact.form.submit}
                  </>
                )}
              </Button>
            </form>
          </div>

          {/* Contact Info */}
          <div className="flex flex-col justify-center">
            <div className="space-y-8">
              {contactInfo.map((item) => {
                const IconComponent = item.icon;
                return (
                  <div key={item.label} className="flex items-start gap-5 group">
                    <div className="w-14 h-14 bg-[#1e3a5f] rounded-lg flex items-center justify-center flex-shrink-0 group-hover:bg-[#7dd3fc] transition-colors">
                      <IconComponent className="w-6 h-6 text-[#7dd3fc] group-hover:text-[#1a1c1b] transition-colors" />
                    </div>
                    <div>
                      <span className="text-[#888680] text-sm font-semibold uppercase tracking-wider block mb-1">
                        {item.label}
                      </span>
                      <span className="text-[#7dd3fc] text-lg font-medium">
                        {item.value}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Extra CTA */}
            <div className="mt-12 p-6 bg-[#1e3a5f]/30 border border-[#1e3a5f] rounded-lg">
              <div className="flex items-center gap-3 mb-3">
                <CheckCircle className="w-6 h-6 text-[#7dd3fc]" />
                <span className="text-[#7dd3fc] font-bold text-lg">{t.contact.cta.title}</span>
              </div>
              <p className="text-[#888680] text-base">
                {t.contact.cta.description}
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
